import { useCallback, useMemo, type Dispatch, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { getOrderedWeekdays } from "../../../../../../helpers/getOrderedWeekdays";
import { firstToUpper } from "../../../../../../../../../utils/helpers/string";
import { cn } from "../../../../../../../../../lib/utils";
import type { Weekday } from "../../../../../../../../../components/shared/inputs/WeekdaySelect/types";

type WeeklyProps = {
    days: Weekday[];
    setDays: Dispatch<SetStateAction<Weekday[]>>;
};

function Weekly({ days, setDays }: WeeklyProps) {
    const { t } = useTranslation();
    const firstDay = moment.localeData().firstDayOfWeek();
    const weekdays = getOrderedWeekdays();

    const weekdaysMin = useMemo(() => {
        return [
            ...moment.weekdaysMin().slice(firstDay),
            ...moment.weekdaysMin().slice(0, firstDay),
        ]
    }, [firstDay]);

    const onDaysClick = useCallback((day: Weekday) => {
        setDays((prev) => {
            const exist = prev?.find((item) => item === day);
            if (exist) {
                return prev.filter((item) => item !== day);
            } else {
                return [...prev, day];
            }
        })
    }, [setDays]);

    return (
        <div className="tw-mb-4">
            <div className="tw-flex tw-items-center tw-gap-4 tw-mb-1">
                <div className="tw-text-neutral-900 tw-text-sm">
                    {firstToUpper(t("on"))}
                </div>
                <div className="tw-flex tw-gap-2">
                    {weekdays.map((day, index) => {
                        const isSelected = days?.find((item) => item === day);
                        return (
                            <span
                                key={day}
                                className={cn(
                                    "tw-border-primary tw-border tw-rounded-full tw-w-10 tw-h-10 tw-flex tw-justify-center tw-items-center tw-cursor-pointer tw-select-none tw-transition-colors tw-text-sm tw-font-medium",
                                    isSelected ? "tw-bg-primary tw-text-white" : "tw-text-primary hover:tw-bg-primary/10"
                                )}
                                onClick={() => onDaysClick(day)}
                            >
                                {firstToUpper(weekdaysMin[index])}
                            </span>
                        )
                    })}
                </div>
            </div>
            {days?.length === 0 && (
                <div className="tw-text-red-500 tw-text-sm">
                    {t("required-day")}
                </div>
            )}
        </div>
    )
}

export default Weekly;
